import { computed } from 'vue'
import { useImage, useRuntimeConfig } from '#imports'
import { IMAGE_PRESETS, type ImagePreset } from '../types/imagePresets'
import { withTouchLayoutSizes } from '../lib/pageZoom'
import type { ImageFieldType } from '@api/types.gen'
import { ImagePresetKey } from '@api/types.gen'

// 1. Export the interface so your components can easily type their v-models
export interface ImageFieldData extends ImageFieldType {
  type: 'image_field'
  id: string
  presetName?: ImagePresetKey
  customSettings?: Partial<ImagePreset>
  width?: number
  height?: number
}

// 2. Single source of truth for generating image attributes
export function resolveImagePreset(img: ReturnType<typeof useImage>, imageField: ImageFieldData) {
  if (!imageField || !imageField.id) return null

  // Construct URL
  const url = `${import.meta.env.VITE_MEDIA_HOST || ''}/api/media/${imageField.id}`

  // Vector sources (SVG) bypass the responsive pipeline entirely — sharp/IPX
  // would otherwise rasterize them to webp during static build. In the static
  // build the file is bundled into public/images/ at build time and served
  // locally; the editor preview falls back to the live API URL.
  if (imageField.mimeType === 'image/svg+xml') {
    const runtime = useRuntimeConfig()
    const bundledFile = runtime.public.bundledSvgs?.[imageField.id]
    const svgUrl = bundledFile
      ? `${(runtime.app?.baseURL ?? '/').replace(/\/$/, '')}/images/${bundledFile}`
      : url
    return {
      imgAttrs: { src: svgUrl, srcset: undefined, sizes: undefined },
      cssBackground: { backgroundImage: `url('${svgUrl}')`, backgroundColor: 'var(--bg-surface)' },
      cssBackgroundFallback: { backgroundImage: `url('${svgUrl}')` },
    }
  }

  const presetName: ImagePresetKey = imageField.presetName ?? ImagePresetKey.LARGE
  let preset: ImagePreset

  // Safely narrow the type so TypeScript stops yelling
  if (presetName === 'custom') {
    // Start with a safe default (large), then merge the custom overrides on top
    preset = { ...IMAGE_PRESETS.large, ...(imageField.customSettings || {}) }
  } else {
    // TypeScript now knows presetName is strictly one of the defined preset keys
    preset = IMAGE_PRESETS[presetName] ?? IMAGE_PRESETS.large
  }

  const isOriginal = presetName === ImagePresetKey.ORIGINAL

  // Generate Nuxt Image attributes
  const generated = img.getSizes(url, {
    sizes: preset.layout === 'fluid' ? preset.sizes : undefined,
    modifiers: {
      width: preset.layout === 'fixed' ? preset.width : undefined,
      format: isOriginal ? undefined : 'webp',
      quality: isOriginal ? undefined : preset.quality,
    },
  })

  // Format CSS Backgrounds
  const cssImageSet = `image-set(${generated.srcset
    .split(', ')
    .map((rule: string) => {
      const [path, descriptor] = rule.split(' ')
      return `url('${path}') ${descriptor}`
    })
    .join(', ')})`

  return {
    imgAttrs: {
      src: generated.src,
      srcset: generated.srcset,
      // Touch devices resolve `sizes` at the old viewport meta's layout width, so they
      // download the file they did before the meta became `width=device-width`.
      sizes:
        preset.layout === 'fluid'
          ? withTouchLayoutSizes(generated.sizes, useRuntimeConfig().public.touchLayoutWidth ?? 0)
          : undefined,
    },
    cssBackground: {
      backgroundImage: cssImageSet,
      backgroundColor: 'var(--bg-surface)',
    },
    cssBackgroundFallback: {
      backgroundImage: `url('${generated.src}')`,
    },
  }
}

// 3. The Vue Composable Wrapper
export function useImagePreset(imageFieldGetter: () => ImageFieldData | undefined | null) {
  const img = useImage()
  return computed(() => {
    const fieldData = imageFieldGetter()
    if (!fieldData?.id) return null
    return resolveImagePreset(img, fieldData)
  })
}

/* ==========================================
 * HOW TO USE IN A COMPONENT
 * ==========================================
 
<template>
  <img 
    v-if="imageProps" 
    v-bind="imageProps.imgAttrs" 
    alt="My Image" 
  />

  <div 
    v-if="imageProps" 
    :style="imageProps.cssBackground"
    class="hero-banner"
  >
    <h1>Content here</h1>
  </div>
</template>

<script setup lang="ts">
import { useImagePreset, type ImageFieldData } from '../composables/useImagePreset'

// 1. Cleanly type your model using the exported interface
const fields = defineModel<{
  image?: ImageFieldData
}>('fields', { required: true })

// 2. Just pass the getter for the whole object!
const imageProps = useImagePreset(() => fields.value.image)
</script>
*/
